import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClickCapture = this.handleClickCapture.bind(this);
    this.refresh = this.refresh.bind(this);
    this.handleMutation = this.handleMutation.bind(this);
    this.isDragging = false;
    this.hasDragged = false;
    this.pointerStartX = 0;
    this.scrollStartX = 0;
    this.lastDragDeltaX = 0;
    this.activePointerId = null;
    this.isResettingLoop = false;
    this.isRefreshing = false;
    this.isSnapping = false;
    this.loopResetTimer = null;
    this.dragAnimationFrame = null;
    this.pendingScrollLeft = 0;
  }

  connectedCallback() {
    this.refresh();
    this.observeMutations();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.detachEventListeners();
    window.clearTimeout(this.loopResetTimer);
    window.cancelAnimationFrame(this.dragAnimationFrame);
  }

  prepareForSectionMorph() {
    if (!this.container) return;

    this.observer?.disconnect();
    this.removeLoopClones();
    this.cells = [...this.container.querySelectorAll('.carousel-cell')];
  }

  refresh() {
    const previousContainer = this.container;

    this.observer?.disconnect();
    this.container = this.querySelector('[data-carousel-container]');
    this.dotsContainer = this.querySelector('[data-carousel-dots]');

    if (previousContainer && previousContainer !== this.container) {
      this.detachEventListeners(previousContainer);
    }

    if (!this.container) {
      this.observeMutations();
      return;
    }

    this.originalCells = [...this.container.querySelectorAll('.carousel-cell:not([data-upsell-clone])')];

    if (this.originalCells.length === 0) {
      this.observeMutations();
      return;
    }

    this.isRefreshing = true;
    this.removeLoopClones();
    this.createLoopClones();
    this.cells = [...this.container.querySelectorAll('.carousel-cell')];
    this.isRefreshing = false;

    this.originalCells.forEach((item) => item.initialize?.());
    this.prepareDragSurface();
    this.initializeDots();
    this.attachEventListeners();
    this.ensureLoopStartPosition();
    this.updateActiveSlide();
    this.observeMutations();
  }

  initializeDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.hidden = this.originalCells.length < 2;

    if (this.dotsContainer.children.length === this.originalCells.length) return;

    this.dotsContainer.innerHTML = '';

    this.originalCells.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cart-drawer__dot';
      dot.setAttribute('aria-label', `Ir para produto ${index + 1}`);
      dot.addEventListener('click', () => this.goToSlide(index));
      this.dotsContainer.appendChild(dot);
    });
  }

  attachEventListeners() {
    if (!this.container || this.container.dataset.upsellBound === 'true') return;

    this.container.dataset.upsellBound = 'true';
    this.container.addEventListener('scroll', this.handleScroll, { passive: true });
    this.container.addEventListener('pointerdown', this.handlePointerDown);
    this.container.addEventListener('pointermove', this.handlePointerMove);
    this.container.addEventListener('pointerup', this.handlePointerUp);
    this.container.addEventListener('pointercancel', this.handlePointerUp);
    this.container.addEventListener('lostpointercapture', this.handlePointerUp);
    this.container.addEventListener('click', this.handleClickCapture, true);
  }

  detachEventListeners(container = this.container) {
    if (!container) return;

    container.removeEventListener('scroll', this.handleScroll);
    container.removeEventListener('pointerdown', this.handlePointerDown);
    container.removeEventListener('pointermove', this.handlePointerMove);
    container.removeEventListener('pointerup', this.handlePointerUp);
    container.removeEventListener('pointercancel', this.handlePointerUp);
    container.removeEventListener('lostpointercapture', this.handlePointerUp);
    container.removeEventListener('click', this.handleClickCapture, true);
    delete container.dataset.upsellBound;
  }

  handleScroll() {
    window.requestAnimationFrame(() => {
      if (!this.isDragging && !this.isSnapping) this.normalizeLoopPosition();
      this.updateActiveSlide();
    });
  }

  handlePointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button, input, label, select, textarea')) return;

    this.isDragging = true;
    this.hasDragged = false;
    this.activePointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.scrollStartX = this.container.scrollLeft;
    this.lastDragDeltaX = 0;
    this.pendingScrollLeft = this.scrollStartX;
    this.container.classList.add('is-dragging');
    this.container.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  handlePointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.pointerStartX;
    this.lastDragDeltaX = deltaX;
    if (Math.abs(deltaX) > 4) this.hasDragged = true;
    if (this.hasDragged) event.preventDefault();

    this.pendingScrollLeft = this.scrollStartX - deltaX;
    this.queueDragScroll();
  }

  handlePointerUp(event) {
    if (!this.isDragging || event.pointerId !== this.activePointerId) return;

    this.isDragging = false;
    this.activePointerId = null;
    window.cancelAnimationFrame(this.dragAnimationFrame);
    this.dragAnimationFrame = null;
    this.container.classList.remove('is-dragging');
    this.container.releasePointerCapture?.(event.pointerId);
    this.snapToNearestSlide();
  }

  handleClickCapture(event) {
    const target = event.target instanceof Element ? event.target : null;
    const cloneCell = target?.closest('[data-upsell-clone]');
    const cloneAddButton = cloneCell && target?.closest('[data-add-to-cart]');

    if (cloneAddButton) {
      event.preventDefault();
      event.stopPropagation();

      const originalIndex = Number(cloneCell.dataset.upsellSlideIndex || 0);
      this.originalCells[originalIndex]?.querySelector('[data-add-to-cart]')?.click();
      return;
    }

    const isNonActionLink = target?.closest('a');

    if (isNonActionLink) {
      event.preventDefault();
      event.stopPropagation();
      this.hasDragged = false;
      return;
    }

    if (!this.hasDragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.hasDragged = false;
  }

  goToSlide(index) {
    const cell = this.originalCells[index];
    if (!cell) return;

    this.scrollToCell(cell, 'smooth');
  }

  snapToNearestSlide() {
    if (!this.container || this.originalCells.length === 0) return;

    const activeCell = this.getClosestCell();
    const activeIndex = Number(activeCell?.dataset.upsellSlideIndex || 0);

    this.isSnapping = true;
    this.scrollToCell(activeCell || this.originalCells[activeIndex], 'smooth');
    window.clearTimeout(this.loopResetTimer);
    this.loopResetTimer = window.setTimeout(() => {
      this.isSnapping = false;
      this.normalizeLoopPosition();
    }, 360);
  }

  updateActiveSlide() {
    if (!this.container || !this.dotsContainer || !this.cells.length) return;

    const activeCell = this.getClosestCell();
    const activeIndex = Number(activeCell.dataset.upsellSlideIndex || 0);

    [...this.dotsContainer.children].forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }

  removeLoopClones() {
    if (!this.container) return;

    this.container.querySelectorAll('[data-upsell-clone]').forEach((clone) => clone.remove());
  }

  createLoopClones() {
    this.originalCells.forEach((cell, index) => {
      cell.dataset.upsellSlideIndex = String(index);
    });

    if (this.originalCells.length < 2) return;

    const beforeClones = document.createDocumentFragment();
    const afterClones = document.createDocumentFragment();

    this.originalCells.forEach((cell, index) => {
      beforeClones.append(this.createClone(cell, index, 'before'));
      afterClones.append(this.createClone(cell, index, 'after'));
    });

    this.container.prepend(beforeClones);
    this.container.append(afterClones);
  }

  createClone(cell, index, position) {
    const clone = cell.cloneNode(true);

    clone.dataset.upsellClone = position;
    clone.dataset.upsellSlideIndex = String(index);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a, button, input, label, select, textarea').forEach((element) => {
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-hidden', 'true');
    });

    return clone;
  }

  prepareDragSurface() {
    this.querySelectorAll('.carousel-cell a, .carousel-cell img').forEach((element) => {
      element.setAttribute('draggable', 'false');
    });
  }

  ensureLoopStartPosition() {
    if (!this.container || this.originalCells.length < 2) return;

    const firstRealCell = this.originalCells[0];
    this.jumpToCell(firstRealCell);
  }

  handleLoopBoundaries() {
    this.normalizeLoopPosition();
  }

  queueDragScroll() {
    if (this.dragAnimationFrame) return;

    this.dragAnimationFrame = window.requestAnimationFrame(() => {
      this.dragAnimationFrame = null;
      this.container.scrollLeft = this.pendingScrollLeft;
      this.normalizeLoopPosition(true);
    });
  }

  normalizeLoopPosition(adjustDragStart = false) {
    if (this.isResettingLoop || this.isRefreshing || !this.container || this.originalCells.length < 2) return;

    const metrics = this.getLoopMetrics();
    if (!metrics) return;

    const { firstOriginalLeft, firstAfterCloneLeft, loopDistance } = metrics;
    const scrollLeft = this.container.scrollLeft;
    const tolerance = 8;
    let nextScrollLeft = scrollLeft;

    if (scrollLeft < firstOriginalLeft - tolerance) {
      nextScrollLeft = scrollLeft + loopDistance;
    } else if (scrollLeft >= firstAfterCloneLeft + tolerance) {
      nextScrollLeft = scrollLeft - loopDistance;
    }

    if (nextScrollLeft === scrollLeft) return;

    const offsetDelta = nextScrollLeft - scrollLeft;
    this.isResettingLoop = true;
    this.container.classList.add('is-loop-resetting');
    this.container.scrollLeft = nextScrollLeft;

    if (adjustDragStart) {
      this.scrollStartX += offsetDelta;
    }

    window.requestAnimationFrame(() => {
      this.container.classList.remove('is-loop-resetting');
      this.isResettingLoop = false;
      this.updateActiveSlide();
    });
  }

  getLoopMetrics() {
    const firstOriginalCell = this.originalCells[0];
    const firstAfterClone = this.container.querySelector('[data-upsell-clone="after"][data-upsell-slide-index="0"]');

    if (!firstOriginalCell || !firstAfterClone) return null;

    const firstOriginalLeft = firstOriginalCell.offsetLeft - this.container.offsetLeft;
    const firstAfterCloneLeft = firstAfterClone.offsetLeft - this.container.offsetLeft;
    const loopDistance = firstAfterCloneLeft - firstOriginalLeft;

    if (loopDistance <= 0) return null;

    return {
      firstOriginalLeft,
      firstAfterCloneLeft,
      loopDistance,
    };
  }

  handleDragLoopBoundaries() {
    this.normalizeLoopPosition(true);
  }

  jumpToCell(cell) {
    this.isResettingLoop = true;
    this.container.classList.add('is-loop-resetting');
    this.container.scrollTo({
      left: cell.offsetLeft - this.container.offsetLeft,
      behavior: 'auto',
    });
    window.requestAnimationFrame(() => {
      this.container.classList.remove('is-loop-resetting');
      this.isResettingLoop = false;
      this.updateActiveSlide();
    });
  }

  scrollToCell(cell, behavior = 'smooth') {
    if (!cell) return;

    this.container.scrollTo({
      left: cell.offsetLeft - this.container.offsetLeft,
      behavior,
    });
  }

  getClosestCell() {
    const containerLeft = this.container.getBoundingClientRect().left;

    return this.cells.reduce((closestCell, cell) => {
      const currentDistance = Math.abs(cell.getBoundingClientRect().left - containerLeft);
      const closestDistance = Math.abs(closestCell.getBoundingClientRect().left - containerLeft);

      return currentDistance < closestDistance ? cell : closestCell;
    }, this.cells[0]);
  }

  observeMutations() {
    this.observer?.disconnect();
    this.observer = new MutationObserver(this.handleMutation);
    this.observer.observe(this, { childList: true, subtree: true });
  }

  handleMutation(mutations) {
    if (this.isRefreshing) return;

    const shouldRefresh = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;

      if (target?.closest('[data-upsell-clone]')) return false;
      if (target?.closest('[data-carousel-dots]')) return false;

      return [...mutation.addedNodes, ...mutation.removedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches('.carousel-cell, [data-carousel-container], [data-carousel-dots]') ||
            node.querySelector('.carousel-cell, [data-carousel-container], [data-carousel-dots]'))
      );
    });

    if (!shouldRefresh) return;

    window.requestAnimationFrame(this.refresh);
  }
}

class ProductItem extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-upsell-clone')) return;

    this.initialize();

    this.observer?.disconnect();
    this.observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => this.initialize());
    });
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.detachListeners();
  }

  initialize() {
    this.detachListeners();
    this.variants = this.parseVariants();
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.variantInput = this.querySelector('input[name="id"]');
    this.error = this.querySelector('[data-upsell-error]');
    this.variantOptionInputs = [...this.querySelectorAll('input[data-option-position]')];

    this.addButton?.addEventListener('click', this.handleAddToCart);
    this.variantOptionInputs.forEach((input) => {
      input.addEventListener('change', this.handleVariantChange);
    });

    this.updateSelectedVariant();
  }

  detachListeners() {
    this.addButton?.removeEventListener('click', this.handleAddToCart);
    this.variantOptionInputs?.forEach((input) => {
      input.removeEventListener('change', this.handleVariantChange);
    });
  }

  parseVariants() {
    try {
      return JSON.parse(this.dataset.variants || '[]');
    } catch (_error) {
      return [];
    }
  }

  handleVariantChange = (event) => {
    const input = event.currentTarget;
    const wrapper = input.closest('.variant-wrapper');
    const label = wrapper?.querySelector('.variant__label--select-variant');

    if (label) label.textContent = input.value;

    this.updateSelectedVariant();
  };

  updateSelectedVariant() {
    const selectedOptions = [...this.querySelectorAll('input[data-option-position]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);

    const fallbackVariantId = this.dataset.currentVariantId;
    const selectedVariant =
      selectedOptions.length > 0
        ? this.variants.find((variant) =>
            selectedOptions.every((option, index) => variant.options?.[index] === option)
          )
        : this.variants.find((variant) => String(variant.id) === fallbackVariantId);

    const variantId = selectedVariant?.id || fallbackVariantId;

    if (variantId) {
      this.dataset.currentVariantId = String(variantId);
      if (this.variantInput) this.variantInput.value = String(variantId);
    }

    if (this.addButton && selectedVariant) {
      this.addButton.disabled = !selectedVariant.available;
      this.addButton.classList.toggle('is-sold-out', !selectedVariant.available);
    }
  }

  handleAddToCart = async (event) => {
    event.preventDefault();

    const variantId = this.variantInput?.value || this.dataset.currentVariantId;
    if (!variantId || !this.addButton || this.addButton.disabled) return;

    this.setLoading(true);
    this.setError('');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);

    if (sectionIds.length > 0) {
      formData.append('sections', [...new Set(sectionIds)].join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      }).then((result) => result.json());

      if (response.status) {
        const message = response.description || response.message || 'Nao foi possivel adicionar este produto.';
        this.setError(message);
        this.dispatchEvent(new CartErrorEvent('cart-upsell', message, response.description, response.errors));
        return;
      }

      const cartUpsell = this.closest('cart-upsell');
      cartUpsell?.prepareForSectionMorph?.();

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-upsell',
          itemCount: await this.getCartItemCount(response.sections),
          productId: this.dataset.productId,
          variantId,
          sections: response.sections,
        })
      );

      window.requestAnimationFrame(() => {
        if (cartUpsell?.isConnected) cartUpsell.refresh();
      });
    } catch (error) {
      console.error(error);
      this.setError('Nao foi possivel adicionar este produto.');
    } finally {
      this.setLoading(false);
    }
  };

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.addButton) this.addButton.setAttribute('aria-busy', String(isLoading));
  }

  setError(message) {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = !message;
  }

  async getCartItemCount(sections) {
    const sectionHtml = sections ? Object.values(sections).find(Boolean) : '';
    const hiddenCount = sectionHtml
      ? new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
      : '';

    if (hiddenCount) return Number(hiddenCount);

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      return cart.item_count || 0;
    } catch (_error) {
      return 0;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}

if (!customElements.get('product-item')) {
  customElements.define('product-item', ProductItem);
}
